import { useState, useEffect, useCallback } from 'react';

/**
 * useConversationDensity
 *
 * Shared, persisted UI-density state for the therapist conversation view.
 *
 * The therapist conversation interface is composed of sibling components
 * (TopControlBar, ConversationSidebar, etc.) that are rendered by a parent
 * (ConversationView) which does NOT thread density props through. To keep
 * those siblings in sync without touching the parent, this hook persists the
 * density to localStorage and broadcasts changes over a custom window event.
 * Any component that calls this hook will re-render when another instance
 * changes the density.
 *
 * Returns:
 *   density      'comfortable' | 'compact'  (persisted, the "base" layout)
 *   setDensity   (value) => void            persists + broadcasts
 *   focusMode    boolean                    transient (NOT persisted)
 *   toggleFocus  () => void                  flip focus on/off
 *
 * Focus is intentionally transient: it is a "zen / maximize transcript"
 * toggle that should not survive a reload. When focus is active the effective
 * layout the consumer should render is "focus"; when it is off the consumer
 * falls back to `density`.
 *
 * Keyboard: Cmd+. (mac) / Ctrl+. toggles focus; Esc exits focus.
 */

export const DENSITY_STORAGE_KEY = 'therapist_conversation_density';
const DENSITY_EVENT = 'therapist:conversation_density_change';

const VALID_DENSITIES = ['comfortable', 'compact'];

const readStoredDensity = () => {
  try {
    const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (stored && VALID_DENSITIES.includes(stored)) {
      return stored;
    }
  } catch (err) {
    // localStorage can throw in private-mode / sandboxed contexts; fall through
    console.warn('[useConversationDensity] Unable to read density preference:', err);
  }
  return 'comfortable';
};

const useConversationDensity = () => {
  const [density, setDensityState] = useState(readStoredDensity);
  const [focusMode, setFocusMode] = useState(false);

  // Persist + broadcast a new density so sibling hook instances stay in sync.
  const setDensity = useCallback((value) => {
    if (!VALID_DENSITIES.includes(value)) {
      console.warn(`[useConversationDensity] Ignoring invalid density: ${value}`);
      return;
    }
    setDensityState(value);
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, value);
    } catch (err) {
      console.warn('[useConversationDensity] Unable to persist density preference:', err);
    }
    // Notify other instances in this tab. (storage events only fire cross-tab.)
    window.dispatchEvent(new CustomEvent(DENSITY_EVENT, { detail: value }));
  }, []);

  const toggleFocus = useCallback(() => {
    setFocusMode((prev) => !prev);
  }, []);

  const exitFocus = useCallback(() => {
    setFocusMode(false);
  }, []);

  // Keep this instance in sync with same-tab and cross-tab changes.
  useEffect(() => {
    const handleCustom = (event) => {
      const value = event?.detail;
      if (VALID_DENSITIES.includes(value)) {
        setDensityState(value);
      }
    };
    const handleStorage = (event) => {
      if (event.key === DENSITY_STORAGE_KEY && VALID_DENSITIES.includes(event.newValue)) {
        setDensityState(event.newValue);
      }
    };
    window.addEventListener(DENSITY_EVENT, handleCustom);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(DENSITY_EVENT, handleCustom);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // Global keyboard shortcuts: Cmd+. / Ctrl+. toggles focus, Esc exits.
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Cmd+. (mac) or Ctrl+. (win/linux) -> toggle focus mode
      if ((event.metaKey || event.ctrlKey) && event.key === '.') {
        event.preventDefault();
        toggleFocus();
        return;
      }
      // Esc exits focus mode (only acts when focus is active so it does not
      // swallow Esc used by modals/menus when focus is off).
      if (event.key === 'Escape') {
        setFocusMode((prev) => {
          if (prev) {
            return false;
          }
          return prev;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleFocus]);

  return { density, setDensity, focusMode, toggleFocus, exitFocus };
};

export default useConversationDensity;
